import { MapPinIcon } from "@heroicons/react/24/outline";
import {
    CALENDAR_EVENT_TIME_GRANULARITY_MINUTES,
    CalendarDate,
    CalendarEventId,
    CalendarEventType,
    DEFAULT_CALENDAR_EVENT_DURATION_MINUTES,
    formatDuration,
    genEntityId,
    getCurrentTimezone,
    ReferenceContactId,
    UserId,
} from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { DeleteButton } from "components/ui/action-button";
import { AutosavingInput } from "components/ui/autosaving-input";
import {
    CommunityLink,
    ReferenceBusinessLink,
    ReferenceContactLink,
} from "components/ui/entity-link";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { produce } from "immer";
import React, { useCallback, useEffect, useReducer } from "react";
import { useCurrentUserId } from "store/selectors/hooks";
import { CalendarEventDto, VisitParent } from "./types";

type VisitEditorProps =
    | {
          mode: "edit";
          visit: CalendarEventDto;
          onSaved: () => void;
          onClose: () => void;
      }
    | {
          mode: "new";
          parent: VisitParent;
          onSaved: () => void;
          onClose: () => void;
      };

interface FieldUpdate {
    field: keyof CalendarEventDto;
    value: string | null;
}

const DURATION_OPTIONS_MINUTES = [15, 30, 45, 60, 90, 120];

const buildDurationOptions = (currentMinutes: number): number[] => {
    if (DURATION_OPTIONS_MINUTES.includes(currentMinutes)) {
        return DURATION_OPTIONS_MINUTES;
    }
    return [...DURATION_OPTIONS_MINUTES, currentMinutes].sort((a, b) => a - b);
};

interface ComponentState {
    status: "loading" | "saving" | "loaded" | "failed";
    visit: CalendarEventDto | null;
    visitExists: boolean;
    pendingUpdates: FieldUpdate[];
    hasSaved: boolean;
    closeRequested: boolean;
    errorMessage: string;
}

type Action =
    | { type: "setVisit"; visit: CalendarEventDto }
    | { type: "enqueueUpdate"; fieldUpdates: FieldUpdate[] }
    | { type: "startCreate" }
    | { type: "startUpdate" }
    | { type: "requestClose" }
    | { type: "setError"; message: string }
    | { type: "updateField"; field: keyof CalendarEventDto; value: string | null };

const defaultErrorMessage = "The visit failed to save.";

const reducer: React.Reducer<ComponentState, Action> = (p, a): ComponentState => {
    return produce(p, draft => {
        switch (a.type) {
            case "setVisit": {
                draft.visit = a.visit;
                draft.status = "loaded";
                draft.visitExists = true;
                break;
            }
            case "enqueueUpdate": {
                draft.pendingUpdates.push(...a.fieldUpdates);
                break;
            }
            case "startCreate": {
                draft.status = "saving";
                draft.hasSaved = true;
                break;
            }
            case "startUpdate": {
                draft.status = "saving";
                draft.pendingUpdates = [];
                draft.hasSaved = true;
                break;
            }
            case "requestClose": {
                draft.closeRequested = true;
                break;
            }
            case "setError": {
                draft.status = "failed";
                draft.errorMessage = a.message || defaultErrorMessage;
                break;
            }
            case "updateField": {
                if (draft.visit) {
                    if (a.field === "eventDuration") {
                        draft.visit.eventDuration = Number(a.value);
                    } else {
                        (draft.visit as Record<string, unknown>)[a.field] = a.value;
                    }
                }
                break;
            }
        }
    });
};

const emptyVisit = (parent: VisitParent, userId: UserId): CalendarEventDto => {
    const now = new Date();
    const todayDate = CalendarDate.fromLocalDate(now);
    const base: CalendarEventDto = {
        id: genEntityId<CalendarEventId>(),
        parentId: parent.id,
        userId,
        type: CalendarEventType.Visit,
        eventDate: todayDate.toString(),
        eventTime: null,
        eventDuration: DEFAULT_CALENDAR_EVENT_DURATION_MINUTES,
        eventTimeZone: null,
        title: null,
        privateNotes: null,
        outcome: null,
        businessId: null,
        businessName: null,
        businessAddress: parent.address,
        communityId: null,
        communityName: null,
        communityAddress: null,
        parentContactName: null,
        dateAdded: now.toISOString(),
        updatedAt: now.toISOString(),
    };
    if (parent.kind === "contact") {
        return { ...base, parentContactName: parent.name };
    }
    return { ...base, businessId: parent.id, businessName: parent.name };
};

const initialStateForExisting = (visit: CalendarEventDto): ComponentState => {
    return {
        status: "loaded",
        visit,
        visitExists: true,
        pendingUpdates: [],
        hasSaved: false,
        closeRequested: false,
        errorMessage: "",
    };
};

const initialStateForNew = (parent: VisitParent, userId: UserId): ComponentState => {
    return {
        status: "loaded",
        visit: emptyVisit(parent, userId),
        visitExists: false,
        pendingUpdates: [],
        hasSaved: false,
        closeRequested: false,
        errorMessage: "",
    };
};

export const VisitEditor: React.FC<VisitEditorProps> = props => {
    const { onSaved, onClose } = props;
    const currentUserId = useCurrentUserId();
    const openedAsExisting = props.mode === "edit";
    const parentIdForNew = props.mode === "new" ? props.parent.id : null;

    const [state, dispatch] = useReducer(
        reducer,
        props.mode === "edit"
            ? initialStateForExisting(props.visit)
            : initialStateForNew(props.parent, currentUserId),
    );

    const currentDurationMinutes =
        state.visit?.eventDuration ?? DEFAULT_CALENDAR_EVENT_DURATION_MINUTES;
    const durationOptions = buildDurationOptions(currentDurationMinutes);

    const trpcClient = trpc.useTRPC();

    const createMutation = useMutation({
        ...trpcClient.calendarEvents.create.mutationOptions(),
        onSuccess: data => {
            dispatch({ type: "setVisit", visit: data });
        },
        onError: error => {
            dispatch({ type: "setError", message: error.message || defaultErrorMessage });
        },
    });

    const updateMutation = useMutation({
        ...trpcClient.calendarEvents.update.mutationOptions(),
        onSuccess: data => {
            dispatch({ type: "setVisit", visit: data });
        },
        onError: error => {
            dispatch({ type: "setError", message: error.message || defaultErrorMessage });
        },
    });

    const deleteMutation = useMutation({
        ...trpcClient.calendarEvents.delete.mutationOptions(),
        onSuccess: () => {
            onSaved();
            onClose();
        },
        onError: error => {
            dispatch({ type: "setError", message: error.message || defaultErrorMessage });
        },
    });

    const doUpdate = useCallback(
        (visitId: CalendarEventId, updatesToMake: FieldUpdate[]) => {
            dispatch({ type: "startUpdate" });

            const params = updatesToMake.reduce(
                (agg, update) => {
                    if (update.field === "eventDate") {
                        return {
                            ...agg,
                            eventDate: update.value as string,
                            eventTimeZone: getCurrentTimezone(),
                        };
                    }
                    if (update.field === "eventTime") {
                        return {
                            ...agg,
                            eventTime: update.value as string | null,
                            eventTimeZone: getCurrentTimezone(),
                        };
                    }
                    if (update.field === "eventDuration") {
                        return {
                            ...agg,
                            eventDuration: Number(update.value),
                        };
                    }
                    if (update.field === "title") {
                        return { ...agg, title: update.value };
                    }
                    if (update.field === "privateNotes") {
                        return { ...agg, privateNotes: update.value };
                    }
                    if (update.field === "outcome") {
                        return { ...agg, outcome: update.value };
                    }
                    if (update.field === "userId") {
                        return {
                            ...agg,
                            userId: update.value as UserId,
                        };
                    }
                    return agg;
                },
                { id: visitId },
            );

            updateMutation.mutate(params);
        },
        [updateMutation],
    );

    const handleFieldChange = useCallback(
        (field: keyof CalendarEventDto, value: string | null) => {
            dispatch({ type: "updateField", field, value });

            const fieldUpdate: FieldUpdate = { field, value };

            if (state.status === "saving") {
                dispatch({ type: "enqueueUpdate", fieldUpdates: [fieldUpdate] });
            } else if (state.status === "loaded") {
                if (state.visitExists && state.visit) {
                    const updatesToMake = [...state.pendingUpdates, fieldUpdate];
                    doUpdate(state.visit.id, updatesToMake);
                } else {
                    const resolvedEventDate =
                        field === "eventDate"
                            ? (value as string)
                            : (state.visit?.eventDate ?? "");

                    if (!resolvedEventDate) {
                        return;
                    }

                    if (parentIdForNew === null) {
                        return;
                    }
                    dispatch({ type: "startCreate" });
                    dispatch({ type: "enqueueUpdate", fieldUpdates: [fieldUpdate] });
                    createMutation.mutate({
                        parentId: parentIdForNew,
                        userId: (state.visit?.userId as UserId) ?? currentUserId,
                        eventDate: resolvedEventDate,
                        eventTime:
                            field === "eventTime"
                                ? (value as string | null)
                                : state.visit?.eventTime,
                        eventDuration:
                            field === "eventDuration"
                                ? Number(value)
                                : (state.visit?.eventDuration ??
                                  DEFAULT_CALENDAR_EVENT_DURATION_MINUTES),
                        title: field === "title" ? value : state.visit?.title,
                        privateNotes:
                            field === "privateNotes" ? value : state.visit?.privateNotes,
                        eventTimeZone: getCurrentTimezone(),
                    });
                }
            }
        },
        [
            state.status,
            state.visitExists,
            state.visit,
            state.pendingUpdates,
            parentIdForNew,
            currentUserId,
            createMutation,
            doUpdate,
        ],
    );

    const handleDelete = useCallback(() => {
        if (state.visit && state.visitExists) {
            deleteMutation.mutate({ id: state.visit.id });
        }
    }, [state.visit, state.visitExists, deleteMutation]);

    const handleClose = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            dispatch({ type: "requestClose" });
        },
        [dispatch],
    );

    useEffect(() => {
        if (state.status === "loaded" && state.visit && state.hasSaved) {
            if (state.pendingUpdates.length > 0) {
                doUpdate(state.visit.id, state.pendingUpdates);
            } else {
                onSaved();
            }
        }
    }, [state.visit]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (
            state.closeRequested &&
            state.status === "loaded" &&
            state.pendingUpdates.length === 0
        ) {
            onClose();
        }
        if (state.closeRequested && state.status === "failed") {
            onClose();
        }
    }, [state.closeRequested, state.status, state.pendingUpdates.length, onClose]);

    return (
        <Panel type={PanelType.medium} headerText="" onDismiss={onClose} isOpen={true}>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                        {state.visitExists ? "Edit Visit" : "Add Visit"}
                    </h2>
                    {state.visitExists ? (
                        <DeleteButton
                            backgroundColor="bg-white"
                            onClick={handleDelete}
                            confirm={{
                                title: "Delete Visit",
                                message: "Are you sure you want to delete this visit?",
                            }}
                        />
                    ) : null}
                </div>
                <div className="space-y-1">
                    {state.visit?.parentContactName ? (
                        <div>
                            <ReferenceContactLink
                                referenceContactId={
                                    state.visit.parentId as ReferenceContactId
                                }
                                name={state.visit.parentContactName}
                            />
                        </div>
                    ) : null}
                    {state.visit?.businessId && state.visit?.businessName ? (
                        <div>
                            <ReferenceBusinessLink
                                referenceBusinessId={state.visit.businessId}
                                name={state.visit.businessName}
                            />
                        </div>
                    ) : null}
                    {state.visit?.communityId && state.visit?.communityName ? (
                        <div>
                            <CommunityLink
                                communityId={state.visit.communityId}
                                name={state.visit.communityName}
                            />
                        </div>
                    ) : null}
                    {(state.visit?.businessAddress ?? state.visit?.communityAddress) ? (
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                            <MapPinIcon
                                className="w-4 h-4 flex-shrink-0"
                                aria-hidden="true"
                            />
                            <span>
                                {state.visit.businessAddress ??
                                    state.visit.communityAddress}
                            </span>
                        </div>
                    ) : null}
                </div>
                <AutosavingInput
                    label="Purpose"
                    initial={state.visit?.title || ""}
                    onCommit={value => handleFieldChange("title", value || null)}
                />

                <div>
                    <label className="block mb-1">Date</label>
                    <input
                        type="date"
                        required
                        value={state.visit?.eventDate || ""}
                        onChange={e => {
                            if (e.target.value) {
                                handleFieldChange("eventDate", e.target.value);
                            }
                        }}
                        className="form-input rounded-sm md:rounded w-full disabled:bg-gray-100 border-gray-300 hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300"
                    />
                </div>

                <div>
                    <label className="block mb-1">Time (optional)</label>
                    <input
                        type="time"
                        step={CALENDAR_EVENT_TIME_GRANULARITY_MINUTES * 60}
                        value={state.visit?.eventTime || ""}
                        onChange={e =>
                            handleFieldChange("eventTime", e.target.value || null)
                        }
                        className="form-input rounded-sm md:rounded w-full disabled:bg-gray-100 border-gray-300 hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300"
                    />
                </div>

                <div>
                    <Select
                        label="Duration"
                        disabled={!state.visit?.eventTime}
                        value={String(currentDurationMinutes)}
                        onChange={e =>
                            handleFieldChange("eventDuration", e.target.value)
                        }>
                        {durationOptions.map(minutes => (
                            <option key={minutes} value={String(minutes)}>
                                {formatDuration(minutes)}
                            </option>
                        ))}
                    </Select>
                </div>

                <UserSelect
                    label="Assigned to"
                    value={state.visit?.userId as SelectableUser}
                    includeEveryone={false}
                    onChange={userId => {
                        if (userId) {
                            handleFieldChange("userId", userId as string);
                        }
                    }}
                />

                <AutosavingInput
                    label="Internal Notes"
                    multiLine={true}
                    initial={state.visit?.privateNotes || ""}
                    onCommit={value => handleFieldChange("privateNotes", value || null)}
                />

                {openedAsExisting ? (
                    <AutosavingInput
                        label="Outcome"
                        multiLine={true}
                        initial={state.visit?.outcome || ""}
                        placeholder="Recap of what happened during the visit"
                        onCommit={value => handleFieldChange("outcome", value || null)}
                    />
                ) : null}

                {state.errorMessage ? (
                    <div className="text-red-600 text-sm">{state.errorMessage}</div>
                ) : null}

                <div className="flex justify-end items-center pt-4">
                    <div className="flex items-center space-x-2">
                        {state.closeRequested ? <Spinner /> : null}
                        <PrimaryButton
                            onClick={handleClose}
                            disabled={state.closeRequested}>
                            Close
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </Panel>
    );
};
